import React, { useEffect, useMemo, useRef, useState } from 'react';
import MaterialIcon from 'react/elements/MaterialIcon';
import searchMealIdeas, { findIdeaNamed } from 'react/components/calendar/searchMealIdeas';

/**
 * A search box over anything with a `name`: meal ideas on the calendar, meal idea groups in an
 * idea's menu. Tapping a match picks it. Given `onCreate`, when nothing is called exactly what was
 * typed, the last option creates one by that name instead.
 *
 * The options are one list for the keyboard: arrows move through matches and the create option
 * together, and Enter takes the highlighted one. The first match that can be picked is highlighted to
 * begin with, so Enter on a close typo picks what it was meant to be rather than creating a
 * near-duplicate. An item given twice is listed once.
 *
 * An item `isTaken` says is already there still shows, marked with `takenNote` and not pickable, so
 * that searching for it finds it rather than offering to create another by the same name.
 *
 * Given `suggestions`, those are what shows before anything is typed, under `suggestionsHeading`,
 * in place of every item. With none to show, a blank search lists every item, or, with
 * `listAllWhenBlank` false, nothing until something is typed: a list too long to scroll is no help.
 * `createLabel` words the create option, and `maxResults` keeps only the best of a search's matches.
 * Given `createDisabled`, a reason, the create option still shows but is disabled, with the reason as
 * its title, as it is offline.
 */
export default function NamePicker({
	items,
	onPick,
	onCreate = null,
	busy = false,
	isTaken = () => false,
	placeholder = 'Search',
	takenNote = 'Already added',
	emptyText = 'Nothing to choose from.',
	suggestions = null,
	suggestionsHeading = null,
	listAllWhenBlank = true,
	maxResults = null,
	createLabel = (name) => <>Create &ldquo;{name}&rdquo;</>,
	createDisabled = null,
}) {
	const [query, setQuery] = useState('');
	// null until the arrows or the mouse pick one: the first pickable option.
	const [highlighted, setHighlighted] = useState(null);
	const input = useRef(null);

	useEffect(() => {
		if(input.current) {
			input.current.focus();
		}
	}, []);

	const trimmed = query.trim();
	const suggesting = trimmed === '' && !!suggestions && suggestions.length > 0;
	const matches = useMemo(() => {
		if(trimmed === '') {
			if(suggestions && suggestions.length > 0) {
				return suggestions;
			}
			return listAllWhenBlank ? searchMealIdeas(items, '') : [];
		}
		const found = searchMealIdeas(items, query);
		return maxResults ? found.slice(0, maxResults) : found;
	}, [items, query, suggestions, listAllWhenBlank, maxResults]);
	const canCreate = !!onCreate && trimmed !== '' && !findIdeaNamed(items, trimmed);
	const options = [
		...matches.map((item) => ({ key: `item-${item.id}`, item, taken: isTaken(item) }))
			.filter((option, index, all) => all.findIndex((each) => each.key === option.key) === index),
		...(canCreate ? [{ key: 'create', create: trimmed }] : []),
	];
	const active = highlighted === null
		? Math.max(options.findIndex((option) => !option.taken), 0)
		: Math.max(Math.min(highlighted, options.length - 1), 0);

	const choose = (option) => {
		if(busy || !option || option.taken || (option.create && createDisabled)) {
			return;
		}
		if(option.create) {
			onCreate(option.create);
		} else {
			onPick(option.item);
		}
	};

	const keyDown = (event) => {
		if(event.key === 'ArrowDown') {
			event.preventDefault();
			setHighlighted(Math.min(active + 1, options.length - 1));
		} else if(event.key === 'ArrowUp') {
			event.preventDefault();
			setHighlighted(Math.max(active - 1, 0));
		} else if(event.key === 'Enter') {
			event.preventDefault();
			choose(options[active]);
		}
	};

	return (
		<div className='name-picker'>
			<input
				ref={input}
				type='search'
				data-autofocus
				className='name-picker__search browser-default'
				placeholder={placeholder}
				autoComplete='off'
				enterkeyhint='done'
				value={query}
				disabled={busy}
				onChange={(event) => {
					setQuery(event.target.value);
					setHighlighted(null);
				}}
				onKeyDown={keyDown}
			/>
			{suggesting && suggestionsHeading &&
			<div className='name-picker__suggestions-heading'>{suggestionsHeading}</div>
			}
			<ul className='name-picker__options' role='listbox'>
				{options.map((option, index) => (
					<li key={option.key} role='option' aria-selected={index === active}>
						<button
							type='button'
							className={[
								'btn-flat',
								'name-picker__option',
								option.create ? 'name-picker__option--create' : '',
								option.taken ? 'name-picker__option--taken' : '',
								index === active ? 'is-highlighted' : '',
							].filter(Boolean).join(' ')}
							disabled={busy || option.taken || (!!option.create && !!createDisabled)}
							title={option.create && createDisabled ? createDisabled : undefined}
							onMouseEnter={() => setHighlighted(index)}
							onClick={() => choose(option)}
						>
							{option.create ?
								<React.Fragment>
									<MaterialIcon>add</MaterialIcon>
									<span>{createLabel(option.create)}</span>
								</React.Fragment> :
								<React.Fragment>
									<span>{option.item.name}</span>
									{option.taken && <small className='name-picker__taken-note'>{takenNote}</small>}
								</React.Fragment>
							}
						</button>
					</li>
				))}
			</ul>
			{options.length === 0 &&
			<p className='name-picker__empty'>{items.length && trimmed !== '' ? 'Nothing matches.' : emptyText}</p>
			}
		</div>
	);
}
